import React, { ChangeEvent, CSSProperties, useState, useEffect, useRef } from 'react';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { TextField } from '@care/react-component-lib';
import { Box, Button, InputAdornment, Theme, Typography } from '@mui/material';
import { Icon24InfoLocation } from '@care/react-icons';
import createRelativeImageURL from '@/utils/createRelativeImageURL';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import HowItWorks from '@/components/HowItWorks';
import {
  FLOURISH_LEFT_IMAGE_URLS,
  LaunchDarklyEvaluationReasons,
  ServiceIdForMember,
  UI_FEATURE_FLAGS,
} from '@/constants';
import {
  seoSegmentationListingPage_seoSegmentationStats_SEOSegmentationStatsSuccess as SegmentationStats,
  seoSegmentationListingPage_seoSegmentationHeroImage_SEOSegmentationHeroImageSuccess as HeroImageSuccessType,
} from '@/__generated__/seoSegmentationListingPage';
import { SxClassProps } from '@/types/common';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { getZipcodeFromGeoInfo } from '@/utils/getZipcodeFromGeoInfo';
import { usePageAttributes } from './contexts/PageAttributesContext';
import ContentRibbon from './ContentRibbon';
import useFeatureFlag from './hooks/useFeatureFlag';

const getClasses = (styleProps: StyleProps) => ({
  heroContainer: (theme: Theme) => ({
    overflow: 'hidden',
    minHeight: '444px',
    maxWidth: '1440px',
    margin: '0 auto',
    backgroundPosition: 'top left, top right',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'auto, auto',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    backgroundImage: `${
      styleProps.flourishLeftImageURLs && styleProps.largeImageURL
        ? `url(${createRelativeImageURL(
            styleProps.flourishLeftImageURLs.largeImageURL
          )}?im=), url(${createRelativeImageURL(styleProps.largeImageURL)}?im=)`
        : 'none'
    }`,

    [theme.breakpoints.up('lg')]: {
      backgroundPosition: `${styleProps.hkBackgroundPositionLgOrBigger}`,
    },
    [theme.breakpoints.only('sm')]: {
      backgroundSize: '25% auto, 50% auto',
      minHeight: '375px',
    },

    [theme.breakpoints.only('md')]: {
      backgroundSize: '20% auto, 45% auto',
      minWidth: '440px',
      minHeight: '355px',
    },
    [theme.breakpoints.only('xs')]: {
      minHeight: '375px',
      backgroundImage: `${
        styleProps.flourishLeftImageURLs && styleProps.smallImageURL
          ? `url(${createRelativeImageURL(
              styleProps.flourishLeftImageURLs.smallImageURL
            )}?im=), url(${createRelativeImageURL(styleProps.smallImageURL)}?im=)`
          : 'none'
      }`,
      backgroundSize: '35% auto, 45% auto',
    },
  }),
  heroContent: (theme: Theme) => ({
    margin: theme.spacing(-3, 0, 0, 30),
    width: '40%',
    height: 'auto',
    [theme.breakpoints.only('sm')]: {
      width: '60%',
      margin: theme.spacing(0, 0, 0, 8),
    },
    [theme.breakpoints.only('md')]: {
      width: '50%',
      margin: theme.spacing(-3, 0, 0, 22.5),
    },
    [theme.breakpoints.only('xs')]: {
      width: '100%',
      margin: '0 auto',
      padding: theme.spacing(0, 2, 0, 2),
    },
  }),
  heroHeadline: (theme: Theme) => ({
    margin: theme.spacing(9, 0, 6, 30),
    width: '37%',
    color: theme.palette.text.primary,
    lineHeight: '42px',
    fontSize: '2.25rem',
    textAlign: 'left',
    [theme.breakpoints.only('sm')]: {
      width: '35%',
      margin: theme.spacing(14, 0, 0, 8),
      paddingBottom: '16px',
    },

    [theme.breakpoints.only('md')]: { margin: theme.spacing(10, 0, 5, 22.5), width: '28%' },

    [theme.breakpoints.down('xl')]: {
      fontSize: '1.75rem',
      lineHeight: '34px',
    },
    [theme.breakpoints.only('xs')]: {
      width: '50%',
      margin: theme.spacing(10, 0, 0, 2),
      fontSize: '1.625rem',
    },
  }),
  heroImgSubText: (theme: Theme) => ({
    width: '70%',
    color: theme.palette.text.primary,
    textAlign: 'left',
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: '24px',
    margin: theme.spacing(0, 0, 3, 0),

    [theme.breakpoints.only('sm')]: {
      margin: theme.spacing(1, 0, 2, 0),
    },
    [theme.breakpoints.only('xs')]: {
      width: '75%',
      margin: theme.spacing(2, 0, 0, 0),
    },

    '& p:first-of-type': {
      marginTop: '0px',
    },
  }),

  textFieldOverride: (theme: Theme) => ({
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0),
    width: '360px',

    '& .MuiInputAdornment-root': {
      ml: 0,
    },

    '& .MuiInputBase-root': {
      minHeight: '62px',
    },

    [theme.breakpoints.only('xs')]: {
      paddingTop: 2,
      pb: 2,
      width: '100%',
    },

    // removes number arrows from Chrome, Safari, Edge, Opera
    '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      appearance: 'none',
      margin: 0,
    },
    // removes number arrows from Firefox
    '& input[type=text]': {
      cursor: 'auto',
      padding: theme.spacing(2, 2, 2, 0),
    },
  }),

  positionStart: (theme: Theme) => ({
    margin: theme.spacing(0, 0, 0, 2),
  }),

  ctaBtn: (theme: Theme) => ({
    fontWeight: 700,
    marginBottom: 4,
    padding: `${theme.spacing(1, 2)} !important`,
    width: '360px',

    [theme.breakpoints.only('xs')]: {
      width: '100%',
    },

    [theme.breakpoints.up('sm')]: {
      mt: 2,
    },
  }),
});

interface HeroProps {
  enrollmentURL: string;
  headline: string;
  heroImage: HeroImageSuccessType['heroImage'];
  searchFiltersRef: React.Ref<HTMLDivElement>;
  subHeadline: string;
  segmentationStats: SegmentationStats['caregiverStats'];
  userGeoInfo: string;
  directoryFirstExperience?: boolean;
}

interface StyleProps extends CSSProperties {
  flourishLeftImageURLs: typeof FLOURISH_LEFT_IMAGE_URLS | undefined;
  largeImageURL: string | undefined;
  smallImageURL: string | undefined;
  hkBackgroundPositionLgOrBigger: string;
}

const Hero = ({
  enrollmentURL,
  headline,
  heroImage,
  searchFiltersRef,
  subHeadline,
  segmentationStats,
  userGeoInfo,
  directoryFirstExperience = false,
}: HeroProps) => {
  const { serviceIdForMember, segmentNamePluralLowerCase, segmentNameSingularLowerCase, pageType } =
    usePageAttributes();

  const styleProps: StyleProps = {
    flourishLeftImageURLs: { ...FLOURISH_LEFT_IMAGE_URLS },
    largeImageURL: heroImage?.largeImageURL,
    smallImageURL: heroImage?.smallImageURL,
    hkBackgroundPositionLgOrBigger:
      serviceIdForMember === ServiceIdForMember.HOUSEKEEPING
        ? 'top left, 88% center'
        : 'top left, top right',
  };

  const classes: SxClassProps = getClasses(styleProps);

  const prefillZipCode = (): string => {
    if (pageType === SEOSegmentationPageType.NATIONAL) {
      const match = userGeoInfo?.match(/zip=(\d{5})/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return '';
  };
  const prefillZipCodeForDirectoryFirst = () => {
    return getZipcodeFromGeoInfo(userGeoInfo);
  };

  const zipCodeRegex = /^\d{0,5}$/;
  const prefilledZipCode: string = prefillZipCode();
  const [zip, setZip] = useState({
    zipCode: prefilledZipCode,
  });

  const zipInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (zipInputRef.current) {
      zipInputRef.current.setSelectionRange(
        zipInputRef.current.value.length,
        zipInputRef.current.value.length
      );

      zipInputRef.current.focus();
    }
  }, [zip]);

  const hideGetStartedCta =
    serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION ||
    serviceIdForMember === ServiceIdForMember.CHILD_CARE;

  const enrollmentLink = (() => {
    if (directoryFirstExperience) {
      if (zip.zipCode && zip.zipCode.length === 5) {
        return `${enrollmentURL}&zipcode=${zip.zipCode}`;
      }
      return `${enrollmentURL}&zipcode=${prefillZipCodeForDirectoryFirst()}`;
    }
    if (!directoryFirstExperience && zip.zipCode) {
      return `${enrollmentURL}&zipcode=${zip.zipCode}`;
    }
    return `${enrollmentURL}`;
  })();

  const getCtaText = () => {
    if (hideGetStartedCta) return 'Start matching';
    return 'Get Started';
  };

  const daycareDirectoryFirstFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id
  );

  const handleCtaBtn = () => {
    const amplitudeData = {
      cta_text: getCtaText(),
      cta_type: 'button',
    };

    if (
      daycareDirectoryFirstFlag &&
      daycareDirectoryFirstFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id,
        test_variant: daycareDirectoryFirstFlag.variationIndex,
        service_id_for_member: serviceIdForMember,
        page_template: pageType,
      });
    }

    trackAmplitudeEvent('CTA Interacted', amplitudeData);
  };

  const onZipCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const zipCodeVal = e.target.value;

    if (!zipCodeRegex.test(zipCodeVal)) {
      return;
    }
    setZip((prevValues) => ({
      ...prevValues,
      zipCode: zipCodeVal,
    }));
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      globalThis.location.href = `${enrollmentLink}`;
      handleCtaBtn();
    }
  };

  const renderZipCodeComponent = () => {
    return (
      <>
        <TextField
          id="zipCode"
          name="zipCode"
          inputRef={zipInputRef}
          onKeyUp={handleKeyUp}
          placeholder="ZIP code"
          value={zip.zipCode}
          onChange={onZipCodeChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={classes.positionStart}>
                <Icon24InfoLocation />
              </InputAdornment>
            ),
          }}
          sx={classes.textFieldOverride}
        />
        <Button
          sx={classes.ctaBtn}
          color="primary"
          component="a"
          href={enrollmentLink}
          onClick={handleCtaBtn}
          size="large"
          variant="contained"
          rel="nofollow">
          {getCtaText()}
        </Button>
      </>
    );
  };

  return (
    <>
      <Box role="dialog" data-testid="hero-container" sx={classes.heroContainer}>
        <Typography
          align="center"
          variant="h1"
          color="primary"
          sx={classes.heroHeadline}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(he.decode(headline)),
          }}
        />
        <Box sx={classes.heroContent}>
          <Box
            data-testid="sub-text"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(he.decode(subHeadline)),
            }}
            sx={classes.heroImgSubText}
          />
          {renderZipCodeComponent()}
        </Box>
      </Box>
      <Box ref={searchFiltersRef} />
      {segmentationStats?.avgPayRate?.amount &&
      segmentationStats?.avgReviewRating &&
      segmentationStats?.avgYearsOfExperience &&
      segmentationStats?.totalProviders ? (
        <ContentRibbon
          avgPayRate={segmentationStats.avgPayRate}
          avgYearsOfExperience={segmentationStats.avgYearsOfExperience}
          avgReviewRating={segmentationStats.avgReviewRating}
          totalProviders={segmentationStats.totalProviders}
        />
      ) : (
        <HowItWorks
          serviceIdForMember={serviceIdForMember}
          segmentNamePluralLowerCase={segmentNamePluralLowerCase}
          segmentNameSingularLowerCase={segmentNameSingularLowerCase}
        />
      )}
    </>
  );
};

export default Hero;
